import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Select, Button } from 'antd';
import {
    CaretDownOutlined,
    CaretUpOutlined,
    RightOutlined,
} from '@ant-design/icons';
import backBtn from '../../assets/images/ATBack.svg';
import { RootState } from '../../store';
import { useGetBooksMutation, useGetClassSubjectsQuery } from '../../api';

interface OptionType {
    label: string;
    value: string | number;
    subId?: string;
}

interface BookType {
    classid: string;
    subjectid: string;
    unitsequenceid: string;
    classname: string;
    subjectname: string;
    bookdetails: {
        series: string;
    };
}

interface SubjectType {
    subjectId: number;
    ssId: string;
}

interface ClassSubjectType {
    classId: number;
    termCount: number;
    subjects: SubjectType[];
}

interface RenderSelectProps {
    placeholder: string;
    options: OptionType[];
    value: string | number | null;
    onChange: (value: string, option: OptionType) => void;
}

const RenderSelect: React.FC<RenderSelectProps> = ({
    placeholder,
    options,
    value,
    onChange,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Select
            className="custom-select"
            onChange={(_, option: any) => onChange(option.value, option)}
            placeholder={placeholder}
            value={value}
            onDropdownVisibleChange={setIsOpen}
            suffixIcon={isOpen ? <CaretUpOutlined style={{fontSize:'18px'}}/> : <CaretDownOutlined style={{fontSize:'18px'}}/>}
            style={{ width: '100%', height: 'fit-content' }}
            options={options}
        />
    );
};

const Reports: React.FC = () => {
    const navigate = useNavigate();
    const [classOptions, setClassOptions] = useState<OptionType[]>([]);
    const [subjectOptions, setSubjectOptions] = useState<OptionType[]>([]);
    const [selectedClass, setSelectedClass] = useState<string | null>(null);
    const [selectedSchoolSubject, setSelectedSchoolSubject] = useState<
        string | null
    >(null);
    const [divisionOptions, setDivisionOptions] = useState<any>([]);
    const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
    const [selectedSubjectName, setSelectedSubjectName] = useState<
        string | null
    >(null);
    const [className, setClassName] = useState<string | any>('');
    const [selectedDivision, setSelectedDivision] = useState<string | null>(
        null
    );
    const [usId, setUsId] = useState<string | null>(null);
    const [books, setBooks] = useState<BookType[]>([]);

    const productType = useSelector((state: RootState) => state.product);
    const schoolInfo = useSelector((state: RootState) => state.SchoolInfo.data);
    const { data: classSubjectsResponse } = useGetClassSubjectsQuery({
        school_id: schoolInfo.school.id,
        academic_year_id: schoolInfo.academicyearid,
        board_id: schoolInfo.boardId,
        product: productType,
    });

    const [getBooks] = useGetBooksMutation();


    const fetchBooks = async () => {
        const classSubjects = classSubjectsResponse?.data?.classSubjects?.map(
            (cls: ClassSubjectType) => ({
                class_id: cls.classId,
                subject_ids: cls.subjects.map(
                    (subj: SubjectType) => subj.subjectId
                ),
                term_count: cls.termCount,
            })
        );

        try {
            const result = await getBooks({
                academic_year_id: schoolInfo.academicyearid,
                board_id: schoolInfo.boardId,
                class_subjects: classSubjects,
                platform: productType,
            }).unwrap();

            const techBooks = result?.data?.filter(
                (book: BookType) => book.bookdetails.series === 'TECHBOOK'
            );
            setBooks(techBooks);

            const classSet = new Map<string, BookType>();
            techBooks.forEach(
                (book: BookType) =>
                    !classSet.has(book.classid) &&
                    classSet.set(book.classid, book)
            );

            const classOptions = Array.from(classSet.values()).map((book) => ({
                label: book.classname,
                value: book.classid,
            }));

            setClassOptions(classOptions);
        } catch (err) {
            console.error('Failed to fetch books:', err);
        }
    };

    const handleSubjectChange = (value: string, option: any) => {
        setSelectedSubject(value);
        setSelectedSubjectName(option.label);
        const matchedClass = classSubjectsResponse?.data?.classSubjects.find(
            (cls: any) => String(cls.classId) === String(selectedClass)
        );
        const matchedSubject = matchedClass?.subjects?.find(
            (subj: any) => String(subj.subjectId) === String(value)
        );

        if (matchedSubject) {
            const divisions = matchedSubject.subjectDivisionData.map(
                (div: any) => ({
                    label: div.division_name,
                    value: div.division_name,
                    id: div.id,
                })
            );
            setDivisionOptions(divisions);
        } else setDivisionOptions([]);
        const matchedBook = books.find(
            (book) =>
                String(book.classid) === String(selectedClass) &&
                String(book.subjectid) === String(value)
        );
        if (matchedBook) {
            setUsId(matchedBook.unitsequenceid);
            setClassName(matchedBook?.classname);
        } else {
            setUsId(null);
            setClassName(null);
        }
    };

    const handleDivisionChange = (val: string, option:any) => {
        setSelectedDivision(option.label);
        setSelectedSchoolSubject(option?.id);
    };

    const getSubjectsByClassId = (
        data: BookType[],
        classId: string
    ): OptionType[] => {
        const seen = new Set<string>();
        return data
            .filter((book: BookType) => {
                return (
                    book.classid === classId &&
                    !seen.has(book.subjectid) &&
                    seen.add(book.subjectid)
                );
            })
            .map((book) => ({
                label: book.subjectname,
                value: book.subjectid,
            }));
    };

    useEffect(() => {
        if (selectedClass) {
            const options = getSubjectsByClassId(books, selectedClass);
            setSubjectOptions(options);
        }
    }, [selectedClass, books]);

    useEffect(() => {
        if (classSubjectsResponse?.data?.classSubjects) fetchBooks();
    }, [classSubjectsResponse]);

    useEffect(() => {
        setSelectedSubject(null);
        setSelectedDivision(null);
        setSelectedSchoolSubject(null);
        setUsId(null);
    }, [selectedClass]);

    const handleNavigate = () => {
        const isElga = className?.toLowerCase().includes('elga')
        if (selectedClass && selectedSubject && selectedDivision) {
            navigate(
                `/reports?classId=${selectedClass}&schoolSubjectId=${selectedSchoolSubject}&division=${selectedDivision}&subjectId=${selectedSubject}&ayId=${schoolInfo.academicyearid}&usid=${usId}&source=Active Teach&subjectName=${selectedSubjectName}&isElga=${isElga}`
            );
        }
    };

    return (
        <div className="px-[2.5rem] py-[2rem] h-full w-auto custom-scrollbar">
            <div className="flex gap-4 items-center">
                <div
                    className="border-[3px] border-white rounded-[12px] p-[4px] cursor-pointer"
                    style={{
                        background:
                            'linear-gradient(140deg, #CBDAFE 42.5%, #EBEFF8 98.04%)',
                    }}
                    onClick={() => navigate('/book-library')}
                >
                    <img src={backBtn} alt="Back" className="w-5 h-5" />
                </div>
                <div className="text-[#000000] text-xl">
                    Assignment & Reports
                </div>
            </div>

            <div className="mt-4 flex gap-7">
                <div className="bg-white p-[30px] rounded-2xl w-[50%]">
                    <p className="text-[#000000] text-lg">Select a subject</p>
                    <span className="text-[#666666] text-lg font-[400]">
                        which you want to view report
                    </span>

                    <div className="flex flex-col gap-5 mt-5 ">
                        <RenderSelect
                            placeholder="Select Class"
                            options={classOptions}
                            value={selectedClass}
                            onChange={(val) => setSelectedClass(val)}
                        />
                        <RenderSelect
                            placeholder="Select Subject"
                            options={subjectOptions}
                            value={selectedSubject}
                            onChange={(val, option) => handleSubjectChange(val, option)}
                        />
                        <RenderSelect
                            placeholder="Select Division"
                            options={divisionOptions}
                            value={selectedDivision}
                            onChange={(val: string, option:any) => handleDivisionChange(val,option)}
                        />

                        <Button
                            type="primary"
                            block
                            disabled={
                                !selectedClass ||
                                !selectedSubject ||
                                !selectedDivision
                            }
                            style={{
                                background:
                                    'linear-gradient(90deg, #527FED 0%, #275BDD 100%)',
                                fontWeight: 800,
                                padding: '24px',
                                color: 'white',
                                fontSize:'16px'
                            }}
                            onClick={handleNavigate}
                        >
                            View Report
                            <RightOutlined
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    color: 'white',
                                }}
                            />
                        </Button>
                    </div>
                </div>
                {/* <div className='w-[50%] rounded-2xl bg-white h-auto'></div> */}
            </div>
        </div>
    );
};

export default Reports;
